import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../App';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { Calculator, TrendingUp, ArrowLeft, PiggyBank, Receipt, LineChart } from 'lucide-react';
import { toast } from 'sonner';

const CalculatorsPage = () => {
  const navigate = useNavigate();
  const [activeCalculator, setActiveCalculator] = useState('emi');
  
  // States for all calculators
  const [emiData, setEmiData] = useState({ principal: '', interest_rate: '', tenure_months: '' });
  const [emiResult, setEmiResult] = useState(null);
  
  const [eligibilityData, setEligibilityData] = useState({ annual_income: '', existing_emi: '', loan_amount: '' });
  const [eligibilityResult, setEligibilityResult] = useState(null);
  
  const [fdData, setFdData] = useState({ principal: '', interest_rate: '', tenure_years: '', compound_frequency: 4 });
  const [fdResult, setFdResult] = useState(null);
  
  const [rdData, setRdData] = useState({ monthly_deposit: '', interest_rate: '', tenure_months: '' });
  const [rdResult, setRdResult] = useState(null);
  
  const [sipData, setSipData] = useState({ monthly_investment: '', expected_return_rate: '', tenure_years: '' });
  const [sipResult, setSipResult] = useState(null);
  
  const [taxData, setTaxData] = useState({ annual_income: '', deductions: 0, regime: 'new' });
  const [taxResult, setTaxResult] = useState(null);

  const calculateEMI = async () => {
    if (!emiData.principal || !emiData.interest_rate || !emiData.tenure_months) {
      toast.error('Please fill all fields');
      return;
    }
    try {
      const res = await axios.post(`${API}/calculate-emi`, {
        principal: parseFloat(emiData.principal),
        interest_rate: parseFloat(emiData.interest_rate),
        tenure_months: parseInt(emiData.tenure_months)
      });
      setEmiResult(res.data);
    } catch (e) { toast.error('Failed to calculate'); }
  };

  const checkEligibility = async () => {
    if (!eligibilityData.annual_income || !eligibilityData.loan_amount) {
      toast.error('Please fill required fields');
      return;
    }
    try {
      const res = await axios.post(`${API}/check-eligibility`, {
        annual_income: parseFloat(eligibilityData.annual_income),
        existing_emi: parseFloat(eligibilityData.existing_emi || 0),
        loan_amount: parseFloat(eligibilityData.loan_amount)
      });
      setEligibilityResult(res.data);
    } catch (e) { toast.error('Failed to check'); }
  };

  const calculateFD = async () => {
    if (!fdData.principal || !fdData.interest_rate || !fdData.tenure_years) {
      toast.error('Please fill all fields');
      return;
    }
    try {
      const res = await axios.post(`${API}/calculate-fd`, {
        principal: parseFloat(fdData.principal),
        interest_rate: parseFloat(fdData.interest_rate),
        tenure_years: parseFloat(fdData.tenure_years),
        compound_frequency: parseInt(fdData.compound_frequency)
      });
      setFdResult(res.data);
    } catch (e) { toast.error('Failed to calculate'); }
  };

  const calculateRD = async () => {
    if (!rdData.monthly_deposit || !rdData.interest_rate || !rdData.tenure_months) {
      toast.error('Please fill all fields');
      return;
    }
    try {
      const res = await axios.post(`${API}/calculate-rd`, {
        monthly_deposit: parseFloat(rdData.monthly_deposit),
        interest_rate: parseFloat(rdData.interest_rate),
        tenure_months: parseInt(rdData.tenure_months)
      });
      setRdResult(res.data);
    } catch (e) { toast.error('Failed to calculate'); }
  };

  const calculateSIP = async () => {
    if (!sipData.monthly_investment || !sipData.expected_return_rate || !sipData.tenure_years) {
      toast.error('Please fill all fields');
      return;
    }
    try {
      const res = await axios.post(`${API}/calculate-sip`, {
        monthly_investment: parseFloat(sipData.monthly_investment),
        expected_return_rate: parseFloat(sipData.expected_return_rate),
        tenure_years: parseInt(sipData.tenure_years)
      });
      setSipResult(res.data);
    } catch (e) { toast.error('Failed to calculate'); }
  };

  const calculateTax = async () => {
    if (!taxData.annual_income) {
      toast.error('Please enter annual income');
      return;
    }
    try {
      const res = await axios.post(`${API}/calculate-tax`, {
        annual_income: parseFloat(taxData.annual_income),
        deductions: parseFloat(taxData.deductions || 0),
        regime: taxData.regime
      });
      setTaxResult(res.data);
    } catch (e) { toast.error('Failed to calculate'); }
  };

  const calculators = [
    { key: 'emi', label: 'EMI', icon: Calculator },
    { key: 'eligibility', label: 'Loan Eligibility', icon: TrendingUp },
    { key: 'fd', label: 'Fixed Deposit', icon: PiggyBank },
    { key: 'rd', label: 'Recurring Deposit', icon: PiggyBank },
    { key: 'sip', label: 'SIP', icon: LineChart },
    { key: 'tax', label: 'Income Tax', icon: Receipt }
  ];

  const renderResultCard = (label, value, color = '#FF6B35') => (
    <div>
      <div style={{ fontSize: '0.875rem', color: '#475569', marginBottom: '0.5rem' }}>{label}</div>
      <div style={{ fontSize: '1.75rem', fontWeight: '700', color }}>₹{value.toLocaleString()}</div>
    </div>
  );

  return (
    <div style={{ backgroundColor: '#F8FAFC', minHeight: '100vh' }}>
      <Navbar />

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '4rem 2rem' }}>
        <button 
          onClick={() => navigate('/')}
          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'none', border: 'none', color: '#FF6B35', cursor: 'pointer', marginBottom: '2rem', fontWeight: '600' }}
          data-testid="back-to-home-button"
        >
          <ArrowLeft size={20} /> Back to Home
        </button>

        <div style={{ marginBottom: '3rem' }}>
          <h1 style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', marginBottom: '0.5rem', color: '#0F172A' }}>
            Financial Calculators
          </h1>
          <p style={{ fontSize: '1.125rem', color: '#475569' }}>
            Plan your finances with our comprehensive suite of calculators
          </p>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
          {calculators.map(c => {
            const Icon = c.icon;
            return (
              <button
                key={c.key}
                onClick={() => setActiveCalculator(c.key)}
                className={activeCalculator === c.key ? 'primary-button' : 'outline-button'}
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' }}
                data-testid={`${c.key}-calculator-tab`}
              >
                <Icon size={18} />
                {c.label}
              </button>
            );
          })}
        </div>

        {/* EMI Calculator */}
        {activeCalculator === 'emi' && (
          <div className="calculator-section fade-in" data-testid="emi-calculator-section">
            <h2 style={{ fontSize: '1.5rem', marginBottom: '0.5rem', color: '#0F172A' }}>EMI Calculator</h2>
            <p style={{ color: '#475569', marginBottom: '2rem' }}>Calculate your monthly loan installment</p>
            <div style={{ display: 'grid', gap: '1.5rem', marginBottom: '2rem' }}>
              <div>
                <label className="label-text">Loan Amount (₹)</label>
                <input type="number" className="input-field" value={emiData.principal} onChange={(e) => setEmiData({...emiData, principal: e.target.value})} placeholder="500000" data-testid="emi-principal-input" />
              </div>
              <div>
                <label className="label-text">Interest Rate (% per annum)</label>
                <input type="number" step="0.01" className="input-field" value={emiData.interest_rate} onChange={(e) => setEmiData({...emiData, interest_rate: e.target.value})} placeholder="10.5" data-testid="emi-interest-rate-input" />
              </div>
              <div>
                <label className="label-text">Tenure (Months)</label>
                <input type="number" className="input-field" value={emiData.tenure_months} onChange={(e) => setEmiData({...emiData, tenure_months: e.target.value})} placeholder="60" data-testid="emi-tenure-input" />
              </div>
            </div>
            <button onClick={calculateEMI} className="primary-button" style={{ width: '100%', justifyContent: 'center' }} data-testid="calculate-emi-button">Calculate EMI</button>
            {emiResult && (
              <div style={{ marginTop: '2rem', padding: '2rem', backgroundColor: '#FFF7ED', borderRadius: '1rem' }} data-testid="emi-result">
                <h3 style={{ fontSize: '1.25rem', marginBottom: '1.5rem', color: '#0F172A' }}>Results</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem' }}>
                  {renderResultCard('Monthly EMI', emiResult.monthly_emi)}
                  {renderResultCard('Total Payment', emiResult.total_payment, '#0F172A')}
                  {renderResultCard('Total Interest', emiResult.total_interest, '#004D7A')}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Eligibility Calculator */}
        {activeCalculator === 'eligibility' && (
          <div className="calculator-section fade-in" data-testid="eligibility-calculator-section">
            <h2 style={{ fontSize: '1.5rem', marginBottom: '0.5rem', color: '#0F172A' }}>Loan Eligibility Calculator</h2>
            <p style={{ color: '#475569', marginBottom: '2rem' }}>Check your maximum eligible loan amount</p>
            <div style={{ display: 'grid', gap: '1.5rem', marginBottom: '2rem' }}>
              <div>
                <label className="label-text">Annual Income (₹)</label>
                <input type="number" className="input-field" value={eligibilityData.annual_income} onChange={(e) => setEligibilityData({...eligibilityData, annual_income: e.target.value})} placeholder="1000000" data-testid="eligibility-income-input" />
              </div>
              <div>
                <label className="label-text">Existing Monthly EMI (₹)</label>
                <input type="number" className="input-field" value={eligibilityData.existing_emi} onChange={(e) => setEligibilityData({...eligibilityData, existing_emi: e.target.value})} placeholder="0" data-testid="eligibility-existing-emi-input" />
              </div>
              <div>
                <label className="label-text">Desired Loan Amount (₹)</label>
                <input type="number" className="input-field" value={eligibilityData.loan_amount} onChange={(e) => setEligibilityData({...eligibilityData, loan_amount: e.target.value})} placeholder="2000000" data-testid="eligibility-loan-amount-input" />
              </div>
            </div>
            <button onClick={checkEligibility} className="primary-button" style={{ width: '100%', justifyContent: 'center' }} data-testid="check-eligibility-button">Check Eligibility</button>
            {eligibilityResult && (
              <div style={{ marginTop: '2rem', padding: '2rem', backgroundColor: eligibilityResult.eligible ? '#D1FAE5' : '#FEE2E2', borderRadius: '1rem', border: `2px solid ${eligibilityResult.eligible ? '#FF6B35' : '#EF4444'}` }} data-testid="eligibility-result">
                <h3 style={{ fontSize: '1.25rem', marginBottom: '1rem', color: eligibilityResult.eligible ? '#065F46' : '#991B1B' }}>
                  {eligibilityResult.eligible ? '✓ Eligible' : '✗ Not Eligible'}
                </h3>
                <p style={{ color: eligibilityResult.eligible ? '#065F46' : '#991B1B', marginBottom: '1.5rem' }}>{eligibilityResult.message}</p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem' }}>
                  {renderResultCard('Max Loan Eligible', eligibilityResult.max_loan_amount)}
                  <div>
                    <div style={{ fontSize: '0.875rem', color: '#475569', marginBottom: '0.5rem' }}>Debt-to-Income Ratio</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#0F172A' }}>{(eligibilityResult.debt_to_income_ratio * 100).toFixed(1)}%</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* FD Calculator */}
        {activeCalculator === 'fd' && (
          <div className="calculator-section fade-in" data-testid="fd-calculator-section">
            <h2 style={{ fontSize: '1.5rem', marginBottom: '0.5rem', color: '#0F172A' }}>Fixed Deposit Calculator</h2>
            <p style={{ color: '#475569', marginBottom: '2rem' }}>Calculate FD maturity amount and interest earned</p>
            <div style={{ display: 'grid', gap: '1.5rem', marginBottom: '2rem' }}>
              <div>
                <label className="label-text">Principal Amount (₹)</label>
                <input type="number" className="input-field" value={fdData.principal} onChange={(e) => setFdData({...fdData, principal: e.target.value})} placeholder="100000" data-testid="fd-principal-input" />
              </div>
              <div>
                <label className="label-text">Interest Rate (% per annum)</label>
                <input type="number" step="0.01" className="input-field" value={fdData.interest_rate} onChange={(e) => setFdData({...fdData, interest_rate: e.target.value})} placeholder="7" data-testid="fd-rate-input" />
              </div>
              <div>
                <label className="label-text">Tenure (Years)</label>
                <input type="number" step="0.5" className="input-field" value={fdData.tenure_years} onChange={(e) => setFdData({...fdData, tenure_years: e.target.value})} placeholder="5" data-testid="fd-tenure-input" />
              </div>
              <div>
                <label className="label-text">Compounding Frequency</label>
                <select className="input-field" value={fdData.compound_frequency} onChange={(e) => setFdData({...fdData, compound_frequency: e.target.value})} data-testid="fd-frequency-select">
                  <option value="1">Yearly</option>
                  <option value="2">Half-Yearly</option>
                  <option value="4">Quarterly</option>
                  <option value="12">Monthly</option>
                </select>
              </div>
            </div>
            <button onClick={calculateFD} className="primary-button" style={{ width: '100%', justifyContent: 'center' }} data-testid="calculate-fd-button">Calculate Maturity</button>
            {fdResult && (
              <div style={{ marginTop: '2rem', padding: '2rem', backgroundColor: '#FFF7ED', borderRadius: '1rem' }} data-testid="fd-result">
                <h3 style={{ fontSize: '1.25rem', marginBottom: '1.5rem', color: '#0F172A' }}>FD Maturity Details</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem' }}>
                  {renderResultCard('Maturity Amount', fdResult.maturity_amount)}
                  {renderResultCard('Interest Earned', fdResult.interest_earned, '#004D7A')}
                  {renderResultCard('Total Invested', fdResult.total_invested, '#0F172A')}
                </div>
              </div>
            )}
          </div>
        )}

        {/* RD Calculator */}
        {activeCalculator === 'rd' && (
          <div className="calculator-section fade-in" data-testid="rd-calculator-section">
            <h2 style={{ fontSize: '1.5rem', marginBottom: '0.5rem', color: '#0F172A' }}>Recurring Deposit Calculator</h2>
            <p style={{ color: '#475569', marginBottom: '2rem' }}>Calculate RD maturity amount with monthly deposits</p>
            <div style={{ display: 'grid', gap: '1.5rem', marginBottom: '2rem' }}>
              <div>
                <label className="label-text">Monthly Deposit (₹)</label>
                <input type="number" className="input-field" value={rdData.monthly_deposit} onChange={(e) => setRdData({...rdData, monthly_deposit: e.target.value})} placeholder="5000" data-testid="rd-deposit-input" />
              </div>
              <div>
                <label className="label-text">Interest Rate (% per annum)</label>
                <input type="number" step="0.01" className="input-field" value={rdData.interest_rate} onChange={(e) => setRdData({...rdData, interest_rate: e.target.value})} placeholder="6.5" data-testid="rd-rate-input" />
              </div>
              <div>
                <label className="label-text">Tenure (Months)</label>
                <input type="number" className="input-field" value={rdData.tenure_months} onChange={(e) => setRdData({...rdData, tenure_months: e.target.value})} placeholder="60" data-testid="rd-tenure-input" />
              </div>
            </div>
            <button onClick={calculateRD} className="primary-button" style={{ width: '100%', justifyContent: 'center' }} data-testid="calculate-rd-button">Calculate Maturity</button>
            {rdResult && (
              <div style={{ marginTop: '2rem', padding: '2rem', backgroundColor: '#FFF7ED', borderRadius: '1rem' }} data-testid="rd-result">
                <h3 style={{ fontSize: '1.25rem', marginBottom: '1.5rem', color: '#0F172A' }}>RD Maturity Details</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem' }}>
                  {renderResultCard('Maturity Amount', rdResult.maturity_amount)}
                  {renderResultCard('Interest Earned', rdResult.interest_earned, '#004D7A')}
                  {renderResultCard('Total Invested', rdResult.total_invested, '#0F172A')}
                </div>
              </div>
            )}
          </div>
        )}

        {/* SIP Calculator */}
        {activeCalculator === 'sip' && (
          <div className="calculator-section fade-in" data-testid="sip-calculator-section">
            <h2 style={{ fontSize: '1.5rem', marginBottom: '0.5rem', color: '#0F172A' }}>SIP Calculator</h2>
            <p style={{ color: '#475569', marginBottom: '2rem' }}>Plan your mutual fund SIP investments</p>
            <div style={{ display: 'grid', gap: '1.5rem', marginBottom: '2rem' }}>
              <div>
                <label className="label-text">Monthly Investment (₹)</label>
                <input type="number" className="input-field" value={sipData.monthly_investment} onChange={(e) => setSipData({...sipData, monthly_investment: e.target.value})} placeholder="10000" data-testid="sip-investment-input" />
              </div>
              <div>
                <label className="label-text">Expected Return Rate (% per annum)</label>
                <input type="number" step="0.01" className="input-field" value={sipData.expected_return_rate} onChange={(e) => setSipData({...sipData, expected_return_rate: e.target.value})} placeholder="12" data-testid="sip-rate-input" />
              </div>
              <div>
                <label className="label-text">Tenure (Years)</label>
                <input type="number" className="input-field" value={sipData.tenure_years} onChange={(e) => setSipData({...sipData, tenure_years: e.target.value})} placeholder="10" data-testid="sip-tenure-input" />
              </div>
            </div>
            <button onClick={calculateSIP} className="primary-button" style={{ width: '100%', justifyContent: 'center' }} data-testid="calculate-sip-button">Calculate SIP</button>
            {sipResult && (
              <div style={{ marginTop: '2rem', padding: '2rem', backgroundColor: '#FFF7ED', borderRadius: '1rem' }} data-testid="sip-result">
                <h3 style={{ fontSize: '1.25rem', marginBottom: '1.5rem', color: '#0F172A' }}>SIP Returns</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem' }}>
                  {renderResultCard('Maturity Amount', sipResult.maturity_amount)}
                  {renderResultCard('Wealth Gained', sipResult.wealth_gained, '#004D7A')}
                  {renderResultCard('Total Invested', sipResult.total_invested, '#0F172A')}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Tax Calculator */}
        {activeCalculator === 'tax' && (
          <div className="calculator-section fade-in" data-testid="tax-calculator-section">
            <h2 style={{ fontSize: '1.5rem', marginBottom: '0.5rem', color: '#0F172A' }}>Income Tax Calculator</h2>
            <p style={{ color: '#475569', marginBottom: '2rem' }}>Calculate your income tax liability (FY 2025-26)</p>
            <div style={{ display: 'grid', gap: '1.5rem', marginBottom: '2rem' }}>
              <div>
                <label className="label-text">Annual Income (₹)</label>
                <input type="number" className="input-field" value={taxData.annual_income} onChange={(e) => setTaxData({...taxData, annual_income: e.target.value})} placeholder="1200000" data-testid="tax-income-input" />
              </div>
              <div>
                <label className="label-text">Tax Regime</label>
                <select className="input-field" value={taxData.regime} onChange={(e) => setTaxData({...taxData, regime: e.target.value})} data-testid="tax-regime-select">
                  <option value="new">New Regime (Default)</option>
                  <option value="old">Old Regime</option>
                </select>
              </div>
              {taxData.regime === 'old' && (
                <div>
                  <label className="label-text">Deductions (80C, 80D etc.) (₹)</label>
                  <input type="number" className="input-field" value={taxData.deductions} onChange={(e) => setTaxData({...taxData, deductions: e.target.value})} placeholder="150000" data-testid="tax-deductions-input" />
                </div>
              )}
            </div>
            <button onClick={calculateTax} className="primary-button" style={{ width: '100%', justifyContent: 'center' }} data-testid="calculate-tax-button">Calculate Tax</button>
            {taxResult && (
              <div style={{ marginTop: '2rem', padding: '2rem', backgroundColor: '#FFF7ED', borderRadius: '1rem' }} data-testid="tax-result">
                <h3 style={{ fontSize: '1.25rem', marginBottom: '1.5rem', color: '#0F172A' }}>Tax Calculation ({taxResult.regime} regime)</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1.5rem' }}>
                  {renderResultCard('Taxable Income', taxResult.taxable_income, '#0F172A')}
                  {renderResultCard('Tax Amount', taxResult.tax_amount, '#004D7A')}
                  {renderResultCard('Cess (4%)', taxResult.cess, '#004D7A')}
                  {renderResultCard('Total Tax', taxResult.total_tax, '#EF4444')}
                  {renderResultCard('Take Home', taxResult.take_home, '#FF6B35')}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CalculatorsPage;
